// Compliance & Licensing guides — full article content keyed by slug

export const complianceArticles = {
  'bis-certification-packaged-drinking-water': {
    intro: [
      'If you plan to sell packaged drinking water in India, BIS certification is not optional — it is the law. Every bottle must carry the ISI mark under IS 14543:2016 (Packaged Drinking Water specification), and selling without it invites seizure, penalties and prosecution under the BIS Act.',
      'The good news: the process is entirely manageable when you know the sequence. This guide explains exactly what BIS requires, what it costs, how long it takes, and the mistakes that delay most applications — drawn from helping 200+ plants through certification.'
    ],
    sections: [
      {
        heading: 'What is IS 14543 and Who Needs It?',
        paras: [
          'IS 14543 is the Indian Standard that defines quality, safety and testing requirements for packaged drinking water (other than natural mineral water, which falls under IS 13428). Every manufacturer bottling and selling drinking water — regardless of plant size — must obtain a BIS license and print the ISI mark with their license number (CM/L number) on every label.',
          'Note the distinction: "packaged drinking water" (treated water, IS 14543) covers 99% of Indian plants. "Natural mineral water" (IS 13428) applies only to water bottled at source from protected springs without treatment that alters composition.'
        ]
      },
      {
        heading: 'Infrastructure BIS Expects at Your Plant',
        paras: [
          'BIS certification is granted to a premises, not just a product. Before applying, your plant must have:'
        ],
        bullets: [
          'Complete treatment train: filtration, RO, UV disinfection and ozonation as applicable to your source water',
          'Hygienic filling area with proper flooring, drainage, and separation from other sections',
          'In-house laboratory with the equipment and chemicals prescribed in IS 14543 for daily testing (pH, TDS, hardness, microbiological tests, etc.)',
          'Qualified chemist/microbiologist on rolls (BIS checks qualifications during audit)',
          'Documented quality control records: raw water testing, in-process checks, and finished product testing registers'
        ]
      },
      {
        heading: 'The Step-by-Step Certification Process',
        paras: [
          'The BIS process follows a defined sequence. First, complete your plant installation and get the in-house lab operational. Second, run trial production so samples are available. Third, file the online application on the BIS portal (manakonline.in) with plant layout, machinery list, lab equipment list, chemist qualification documents, water test reports and factory documents. Fourth, a BIS officer conducts a factory inspection — verifying machinery, hygiene, lab capability and drawing samples for independent testing at a BIS-approved laboratory. Fifth, once samples pass, you pay the marking fee and receive your CM/L license, valid initially for one year (extendable up to five) and renewable.',
          'From application to license grant, a well-prepared plant typically completes the process in 30-60 days. Poorly prepared applications stretch to 4-6 months due to inspection non-conformities and repeat sampling.'
        ]
      },
      {
        heading: 'BIS Certification Cost Breakdown',
        paras: [
          'Total first-year cost typically lands between ₹1.5 – 2.5 Lakhs, comprising:'
        ],
        table: {
          headers: ['Cost Component', 'Approximate Amount'],
          rows: [
            ['Application fee', '₹1,000'],
            ['Inspection charges', '₹7,000 – 10,000 per visit'],
            ['Sample testing charges (BIS lab)', '₹15,000 – 25,000'],
            ['Annual license fee', '₹1,000'],
            ['Annual marking fee (unit-based/lump sum)', '₹45,000 – 1,00,000+'],
            ['Lab equipment & chemicals (one-time)', '₹1.5 – 2 Lakhs'],
            ['Consultant charges (optional)', '₹30,000 – 75,000']
          ]
        }
      },
      {
        heading: 'Common Reasons BIS Applications Get Delayed',
        paras: [
          'In our experience, five issues cause the majority of delays: incomplete in-house lab (missing incubator, autoclave or prescribed chemicals), chemist without the required qualification, treatment train not matching IS 14543 requirements for the source water, poor hygienic separation between plant sections, and missing documentation of daily test registers during inspection.',
          'Every one of these is avoidable with proper plant design. This is why we build BIS compliance into the plant layout and machinery specification from day one, and supply the complete lab equipment package with our turnkey projects.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Can I sell packaged water without BIS certification?',
        a: 'No. The ISI mark under IS 14543 is mandatory for packaged drinking water in India. Selling without it is an offence under the BIS Act 2016, punishable with imprisonment up to two years and/or fines starting at ₹2 Lakhs, plus product seizure.'
      },
      {
        q: 'How long does BIS certification take for a water plant?',
        a: 'A well-prepared plant completes it in 30-60 days from application: factory inspection, independent sample testing, and license grant. Preparation of the lab and documentation before applying is the key to staying on this timeline.'
      },
      {
        q: 'What is the total cost of BIS certification?',
        a: 'Budget ₹1.5 – 2.5 Lakhs for the first year including application, inspection, testing and marking fees, plus ₹1.5 – 2 Lakhs one-time for the mandatory in-house laboratory equipment.'
      },
      {
        q: 'Do I need both BIS and FSSAI for a water plant?',
        a: 'Yes. BIS certifies the product against IS 14543, while FSSAI licenses you as a food business operator. Both are mandatory, and FSSAI will not license a packaged water unit that lacks BIS certification.'
      }
    ]
  },

  'fssai-licence-mineral-water-plant': {
    intro: [
      'Packaged drinking water is legally classified as a food product in India, which means every water bottling plant needs an FSSAI license before commercial production. Operating without one attracts penalties up to ₹5 Lakhs and possible imprisonment under the Food Safety and Standards Act.',
      'This guide covers which FSSAI license category applies to your plant, the exact documents required, government fees, and how the FSSAI application interacts with your BIS certification timeline.'
    ],
    sections: [
      {
        heading: 'Which FSSAI License Does a Water Plant Need?',
        paras: [
          'FSSAI has three tiers, and for manufacturers the tier depends on production capacity and turnover. Basic Registration (turnover under ₹12 Lakhs) is meant for petty businesses and is generally NOT applicable to bottling plants. A State License applies to most small and mid-size water plants — manufacturing units with turnover between ₹12 Lakhs and ₹20 Crores. A Central License is required for large plants with turnover above ₹20 Crores, units supplying to central government, railways, airports, or plants engaged in export.',
          'Practically, a new 24-60 BPM plant starts with a State Manufacturing License and upgrades to Central if turnover or supply contracts demand it.'
        ]
      },
      {
        heading: 'Documents Required for FSSAI Manufacturing License',
        paras: [
          'Keep this checklist ready before you apply on the FoSCoS portal (foscos.fssai.gov.in):'
        ],
        bullets: [
          'Form B application, signed by proprietor/partner/director',
          'Blueprint/layout plan of the processing unit showing area dimensions',
          'List of machinery with capacity and installed power',
          'Water test report from an NABL-accredited laboratory',
          'List of directors/partners with ID and address proof',
          'Proof of premises possession (rent agreement/ownership deed)',
          'Food safety management system plan or certificate',
          'BIS license copy (or proof of application, with license before production)',
          'NOC from municipality/local body where applicable'
        ]
      },
      {
        heading: 'FSSAI Fees and Timeline',
        paras: [
          'Government fees are modest: State manufacturing licenses cost ₹3,000 – 5,000 per year depending on capacity, and Central licenses cost ₹7,500 per year. Licenses can be taken for 1 to 5 years at once — taking 5 years saves repeated renewal effort.',
          'Processing typically takes 15-45 days for a State license, including a pre-license inspection of the premises by a Food Safety Officer in most states. Renewal must be applied no later than 30 days before expiry to avoid late fees of ₹100 per day.'
        ]
      },
      {
        heading: 'Labelling Requirements Under FSSAI',
        paras: [
          'Your bottle label must comply with FSSAI packaging and labelling regulations in addition to carrying the ISI mark. Mandatory elements include: product name ("Packaged Drinking Water"), FSSAI license number with logo, ISI mark with CM/L number, batch number, manufacturing date, best-before period, net quantity, MRP, manufacturer name and complete plant address, and customer care details. Nutritional declaration requirements are simplified for water, but font sizes and placement rules still apply.',
          'Design your label once, correctly — reprinting lakhs of labels because the FSSAI logo was undersized is an expensive lesson several new brands learn.'
        ]
      },
      {
        heading: 'Sequencing FSSAI with BIS and Other Approvals',
        paras: [
          'The efficient sequence for a new plant: apply for FSSAI license and pollution board consent as soon as your premises and layout are finalised (both accept applications before machinery arrives), complete plant installation and lab setup, obtain BIS certification with trial production samples, then link the BIS license in your FSSAI record before commercial dispatch. Running these in parallel instead of sequentially saves 45-60 days on your launch timeline.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Is FSSAI mandatory for a mineral water plant?',
        a: 'Yes. Packaged drinking water is a food product under the FSS Act, so every bottling plant needs an FSSAI manufacturing license — in addition to mandatory BIS certification — before commercial production.'
      },
      {
        q: 'What does an FSSAI license cost for a water plant?',
        a: 'Government fees are ₹3,000 – 5,000 per year for a State manufacturing license and ₹7,500 per year for a Central license. Consultant charges, if you use one, add ₹5,000 – 15,000 one-time.'
      },
      {
        q: 'How long does it take to get the FSSAI license?',
        a: 'Typically 15-45 days for a State manufacturing license, including premises inspection. Applying early — as soon as your layout is final — keeps FSSAI off your critical path to launch.'
      },
      {
        q: 'Can I apply for FSSAI before BIS certification?',
        a: 'Yes, and you should. FSSAI applications are accepted with proof of BIS application, though commercial production of packaged water requires the BIS license to be in force. Running both processes in parallel shortens your launch timeline significantly.'
      }
    ]
  },

  'licences-required-mineral-water-bottling-plant': {
    intro: [
      'One of the biggest anxieties for new plant owners is licensing — how many approvals, from whom, in what order, and at what cost? Miss one and your launch stalls; a seized consignment or a pollution board notice can undo months of work.',
      'Here is the complete, practical checklist of every license and approval a mineral water bottling plant needs in India, with realistic costs and timelines, and the sequence that gets you to launch fastest.'
    ],
    sections: [
      {
        heading: 'The Complete License Checklist',
        paras: [
          'Every packaged drinking water plant in India needs the following approvals. Costs are indicative and vary somewhat by state:'
        ],
        table: {
          headers: ['License / Approval', 'Issuing Authority', 'Indicative Cost', 'Timeline'],
          rows: [
            ['BIS Certification (IS 14543)', 'Bureau of Indian Standards', '₹1.5 – 2.5 Lakhs (yr 1)', '30 – 60 days'],
            ['FSSAI Manufacturing License', 'FSSAI / State Food Authority', '₹3,000 – 7,500/yr', '15 – 45 days'],
            ['Consent to Establish (CTE)', 'State Pollution Control Board', '₹10,000 – 40,000', '30 – 60 days'],
            ['Consent to Operate (CTO)', 'State Pollution Control Board', '₹15,000 – 35,000', '30 – 45 days'],
            ['Groundwater NOC', 'CGWA / State Groundwater Dept', '₹10,000 – 50,000', '30 – 90 days'],
            ['Trade License', 'Municipality / Panchayat', '₹2,000 – 10,000', '7 – 30 days'],
            ['GST Registration', 'GST Department', 'Free', '3 – 7 days'],
            ['Factory License (if applicable)', 'State Factories Dept', '₹5,000 – 25,000', '30 days'],
            ['MSME/Udyam Registration', 'Ministry of MSME', 'Free', 'Same day']
          ]
        }
      },
      {
        heading: 'Pollution Board Consents: CTE and CTO Explained',
        paras: [
          'Water bottling plants fall under the Green or Orange category in most states, depending on capacity and effluent handling. You need Consent to Establish (CTE) before construction/installation begins and Consent to Operate (CTO) before production starts. The application covers your water source, RO reject water management plan, and waste disposal arrangements.',
          'The RO reject question matters: a plant bottling 10,000 litres a day may generate 4,000-6,000 litres of reject water. Boards increasingly expect a reuse plan — gardening, floor washing, construction supply or controlled discharge. Building a reject storage tank into your plant design makes this approval straightforward.'
        ]
      },
      {
        heading: 'Groundwater NOC — The One Everyone Forgets',
        paras: [
          'If your plant draws groundwater through a borewell, most states now require a No Objection Certificate from the Central Ground Water Authority (CGWA) or the state groundwater department. Requirements vary by whether your block is classified as safe, semi-critical, critical or over-exploited — plants in over-exploited blocks face stricter conditions including recharge obligations.',
          'Apply early: this NOC frequently has the longest and least predictable timeline of the entire package. Plants using municipal or tanker water supply are generally exempt, but need supply agreements as proof.'
        ]
      },
      {
        heading: 'The Optimal Sequencing Strategy',
        paras: [
          'Run approvals in parallel, not in series. The sequence that minimises time-to-launch:'
        ],
        bullets: [
          'Week 1-2: Udyam registration, GST, trade license application; finalise premises',
          'Week 2-4: Apply CTE (pollution board) and groundwater NOC with plant layout',
          'Week 3-4: Apply FSSAI with layout and machinery list; order machinery',
          'Week 8-12: Machinery installation; set up BIS-compliant laboratory',
          'Week 12-14: Trial production; file BIS application; apply CTO',
          'Week 14-20: BIS inspection and sampling; receive CM/L license; link to FSSAI; begin dispatch'
        ]
      },
      {
        heading: 'Ongoing Compliance After Launch',
        paras: [
          'Licensing is not one-and-done. Maintain daily lab test registers (BIS audits these), renew FSSAI before expiry, file pollution board returns as prescribed, pay the annual BIS marking fee, and update licenses when you add products (e.g., adding 20-litre jars or a new label brand requires BIS/FSSAI endorsement). A simple compliance calendar prevents the lapses that trigger penalties and surprise inspections.'
        ]
      }
    ],
    faqs: [
      {
        q: 'How many licenses are required for a mineral water plant in India?',
        a: 'Eight to nine approvals in total: BIS certification, FSSAI license, pollution board CTE and CTO, groundwater NOC (for borewell users), trade license, GST registration, Udyam/MSME registration, and a factory license where worker thresholds apply.'
      },
      {
        q: 'What is the total licensing cost for a new water plant?',
        a: 'Plan ₹3 – 5 Lakhs all-inclusive for a new plant: BIS is the largest component at ₹1.5 – 2.5 Lakhs including lab-related costs, with pollution consents, groundwater NOC and local licenses making up the balance.'
      },
      {
        q: 'How long does the complete licensing process take?',
        a: 'With parallel processing, 3 – 4 months alongside plant installation. Done sequentially, it can stretch to 8+ months — which is why sequencing and early applications matter more than any other factor.'
      },
      {
        q: 'Do I need a groundwater NOC if I use tanker or municipal water?',
        a: 'Generally no — the NOC applies to groundwater extraction. However, you must document your water source with supply agreements, and your pollution board application still covers water usage and reject management.'
      }
    ]
  }
};
